import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Bell, Check, User, ArrowRightLeft, Clock, Trophy, SkipForward } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';

const TYPE_CONFIG = {
  'pick-made':      { Icon: User,           color: 'text-teal-400'   },
  'your-turn':      { Icon: Clock,          color: 'text-yellow-400' },
  'trade-proposal': { Icon: ArrowRightLeft, color: 'text-blue-400'   },
  'trade-executed':  { Icon: Check,          color: 'text-green-400'  },
  'pick-skipped':   { Icon: SkipForward,    color: 'text-orange-400' },
  'draft-complete':  { Icon: Trophy,         color: 'text-yellow-400' },
};

function formatRelativeTime(isoStr) {
  if (!isoStr) return '';
  const diffMs = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

const DraftNotificationBell = ({ notifications, unreadCount, markRead, markAllRead }) => {
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  const toggleOpen = () => {
    if (!isOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    setIsOpen(prev => !prev);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          bellRef.current && !bellRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const displayCount = unreadCount > 9 ? '9+' : unreadCount;

  const dropdown = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={`${dynastyTheme.components.card.base} z-[99999] w-80 overflow-hidden`}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        right: dropdownPos.right,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700">
        <span className="font-semibold text-white text-sm">Draft Activity</span>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-neutral-400 text-sm">
            No draft activity yet
          </div>
        ) : (
          notifications.map(notif => {
            const config = TYPE_CONFIG[notif.type] || { Icon: Bell, color: 'text-neutral-400' };
            const { Icon } = config;
            return (
              <div
                key={notif.id}
                onClick={() => markRead(notif.id)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 cursor-pointer transition-colors
                  bg-neutral-800/60 border-b border-neutral-700/50
                  ${notif.read
                    ? 'opacity-50 hover:opacity-70 hover:bg-neutral-700/40'
                    : 'hover:bg-neutral-700/40'
                  }`}
              >
                <div className={`mt-0.5 flex-shrink-0 ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs leading-snug ${notif.read ? 'text-neutral-400' : 'text-white'}`}>
                    {notif.message}
                  </p>
                  <p className="text-[10px] text-neutral-400 mt-1">
                    {formatRelativeTime(notif.timestamp)}
                  </p>
                </div>
                {!notif.read && (
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0 mt-1.5" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={bellRef}
        onClick={toggleOpen}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
        title="Draft Activity"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
            {displayCount}
          </span>
        )}
      </button>
      {dropdown}
    </>
  );
};

export default DraftNotificationBell;
